const PREFS_KEY = 'aleno_ops_agent_prefs_v1';

export type AgentPrefs = {
  /** Situation ids the user dismissed (hidden until they clear). */
  dismissedIds: string[];
  /** Situation ids pinned to the top of the brief. */
  pinnedIds: string[];
  /** Free-text “care about” phrases that bias chips / ranking copy. */
  careAbout: string[];
};

const EMPTY: AgentPrefs = {
  dismissedIds: [],
  pinnedIds: [],
  careAbout: [],
};

function safeParse(raw: string | null): AgentPrefs {
  if (!raw) return { ...EMPTY, dismissedIds: [], pinnedIds: [], careAbout: [] };
  try {
    const parsed = JSON.parse(raw) as Partial<AgentPrefs>;
    return {
      dismissedIds: Array.isArray(parsed.dismissedIds)
        ? parsed.dismissedIds.map(String).slice(0, 80)
        : [],
      pinnedIds: Array.isArray(parsed.pinnedIds)
        ? parsed.pinnedIds.map(String).slice(0, 20)
        : [],
      careAbout: Array.isArray(parsed.careAbout)
        ? parsed.careAbout.map(String).slice(0, 12)
        : [],
    };
  } catch {
    return { ...EMPTY, dismissedIds: [], pinnedIds: [], careAbout: [] };
  }
}

export function loadAgentPrefs(): AgentPrefs {
  if (typeof window === 'undefined') {
    return { ...EMPTY, dismissedIds: [], pinnedIds: [], careAbout: [] };
  }
  return safeParse(localStorage.getItem(PREFS_KEY));
}

export function saveAgentPrefs(prefs: AgentPrefs): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

export function dismissSituation(id: string): AgentPrefs {
  const prefs = loadAgentPrefs();
  if (!prefs.dismissedIds.includes(id)) {
    prefs.dismissedIds = [id, ...prefs.dismissedIds].slice(0, 80);
  }
  prefs.pinnedIds = prefs.pinnedIds.filter(x => x !== id);
  saveAgentPrefs(prefs);
  return prefs;
}

export function pinSituation(id: string): AgentPrefs {
  const prefs = loadAgentPrefs();
  if (!prefs.pinnedIds.includes(id)) {
    prefs.pinnedIds = [id, ...prefs.pinnedIds].slice(0, 20);
  }
  prefs.dismissedIds = prefs.dismissedIds.filter(x => x !== id);
  saveAgentPrefs(prefs);
  return prefs;
}

export function unpinSituation(id: string): AgentPrefs {
  const prefs = loadAgentPrefs();
  prefs.pinnedIds = prefs.pinnedIds.filter(x => x !== id);
  saveAgentPrefs(prefs);
  return prefs;
}

/** Extract a short topic from an ask prompt / insight for preference biasing. */
export function careTopicFromPrompt(phrase: string): string | null {
  let t = phrase.trim().replace(/\s+/g, ' ');
  const tellMore = t.match(/^Tell me more about:\s*(.+?)(?:\.\s*Why\b|$)/i);
  if (tellMore) t = tellMore[1].trim();
  if (!t || /^Tell me more\b/i.test(t)) return null;
  if (
    /\bvs (their |this house|the previous)/i.test(t) ||
    /over the last \d+ matching/i.test(t) ||
    /\bclimbed from\b/i.test(t)
  ) {
    return null;
  }
  if (t.length > 60) t = `${t.slice(0, 57)}…`;
  return t;
}

export function addCareAbout(phrase: string): AgentPrefs {
  const prefs = loadAgentPrefs();
  const cleaned = careTopicFromPrompt(phrase);
  if (!cleaned) return prefs;
  prefs.careAbout = [
    cleaned,
    ...prefs.careAbout.filter(c => c.toLowerCase() !== cleaned.toLowerCase()),
  ].slice(0, 12);
  saveAgentPrefs(prefs);
  return prefs;
}

/** Rank situations with pins first and dismissed removed. */
export function applyPrefsToSituations<T extends { id: string; priority: number }>(
  situations: T[],
  prefs: AgentPrefs = loadAgentPrefs(),
): T[] {
  const dismissed = new Set(prefs.dismissedIds);
  const pinned = new Set(prefs.pinnedIds);
  const visible = situations.filter(s => !dismissed.has(s.id));
  return [...visible].sort((a, b) => {
    const ap = pinned.has(a.id) ? 1 : 0;
    const bp = pinned.has(b.id) ? 1 : 0;
    if (ap !== bp) return bp - ap;
    return b.priority - a.priority;
  });
}
